import React, { useEffect, useMemo, useRef, useState } from 'react';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import { FiExternalLink } from 'react-icons/fi';
import BibliothequePlotly from '../../Graphe/AffichageGraphe/BibliothequePlotly/BibliothequePlotly';
import AffichageVue from '../../Vue/AffichageVue/AffichageVue';
import MapPreview from './MapPreview';
import S from './Widget.module.scss';
import WidgetHeaderControls from './WidgetHeaderControls';
import Popup from '../../UI/PopupVue/Popup';
import { DEFAULT_WIDGET_OPTIONS, resolveWidgetOptions } from './widgetOptions';

const ORIGINAL_WIDTH = 900;
const ORIGINAL_HEIGHT = 540;
const DEFAULT_CONTAINER_WIDTH = 575;

const WidgetTableauDeBord = ({
  dashboards = [],
  vuesFavorites = [],
  graphesFavoris = [],
  onGrapheClick,
  availableWidgets = ['dashboard'],
  initialWidget = null,
  sizeVariant = 'large',
  headerContent = null,
  showHeader = true,
  viewData = null,
  viewId = null,
  loading = false,
}) => {
  const hasDashboards = dashboards && dashboards.length > 0;
  const hasVue = vuesFavorites && vuesFavorites.length > 0;
  const hasGraphes = graphesFavoris && graphesFavoris.length > 0;
  const [afficherPopup, setAfficherPopup] = useState(false);
  const ouvrirPopup = () => setAfficherPopup(true);
  const fermerPopup = () => setAfficherPopup(false);
  const resolvedViewId =
    viewId || dashboards[0]?.view_id || viewData?.view?.view_id || null;

  // Lazy mount : n'instancier AffichageVue que lorsque le widget est visible
  const widgetRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const el = widgetRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Reset mapReady quand la vue change
  useEffect(() => {
    if (isVisible) setMapReady(false);
  }, [resolvedViewId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Masquer le skeleton après que la carte a eu le temps de se charger
  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => setMapReady(true), 900);
    return () => clearTimeout(timer);
  }, [isVisible, resolvedViewId]);
  const hasViewData =
    viewData &&
    viewData.view &&
    viewData.layers_list &&
    viewData.data_objects_list_view;

  const widgetOptions = useMemo(
    () => resolveWidgetOptions(availableWidgets),
    [availableWidgets]
  );

  // Marquer les options comme disabled si les données ne sont pas disponibles
  // Ne pas désactiver une option si elle est la seule disponible ou si en chargement
  const widgetOptionsWithDisabled = useMemo(() => {
    const hasGrapheData =
      hasGraphes ||
      (hasViewData &&
        viewData.data_objects_list_view &&
        viewData.data_objects_list_view.some((obj) => obj.viewobj_type === 2));
    const hasPlanData = hasVue || Boolean(resolvedViewId);
    const isSingleOption = widgetOptions.length === 1;

    return widgetOptions.map((option) => {
      // Ne jamais désactiver si c'est la seule option ou si en chargement
      if (isSingleOption || loading) {
        return option;
      }

      if (option.value === 'graphe' && !hasGrapheData) {
        return { ...option, disabled: true };
      }
      if (option.value === 'plan' && !hasPlanData) {
        return { ...option, disabled: true };
      }
      if (option.value === 'dashboard' && !hasDashboards && !hasViewData) {
        return { ...option, disabled: true };
      }
      return option;
    });
  }, [
    widgetOptions,
    hasGraphes,
    hasVue,
    hasDashboards,
    hasViewData,
    viewData,
    resolvedViewId,
    loading,
  ]);
  // Aceita view_name ou dashboard_name
  const firstVue = hasVue ? vuesFavorites[0] : null;

  // Extraire le graphique des données de la vue
  const firstGraphe = useMemo(() => {
    if (hasGraphes) return graphesFavoris[0];
    if (hasViewData && viewData.data_objects_list_view) {
      const graphObjects = viewData.data_objects_list_view.filter(
        (obj) => obj.viewobj_type === 2
      );
      if (graphObjects.length > 0) {
        const graphObj = graphObjects[0];
        // Retourner toute la structure de données du graphique
        return {
          ...graphObj,
          graph_name: graphObj.name || graphObj.data?.config?.title || 'Graphe',
          plotData: graphObj.data, // Manter compatibilidade
        };
      }
    }
    return null;
  }, [hasGraphes, graphesFavoris, hasViewData, viewData]);

  const pickInitialWidget = () => {
    const allowedValues = widgetOptions.map((option) => option.value);
    if (initialWidget && allowedValues.includes(initialWidget)) {
      return initialWidget;
    }
    if (hasDashboards && allowedValues.includes('dashboard')) {
      return 'dashboard';
    }
    if ((hasVue || resolvedViewId) && allowedValues.includes('plan')) {
      return 'plan';
    }
    if ((hasGraphes || hasViewData) && allowedValues.includes('graphe')) {
      return 'graphe';
    }
    return widgetOptions[0]?.value || DEFAULT_WIDGET_OPTIONS[0].value;
  };

  const [selectedWidget, setSelectedWidget] = useState(() =>
    pickInitialWidget()
  );
  const currentWidget =
    widgetOptions.find((option) => option.value === selectedWidget) ||
    widgetOptions[0] ||
    DEFAULT_WIDGET_OPTIONS[0];

  useEffect(() => {
    if (!widgetOptions.some((option) => option.value === selectedWidget)) {
      const fallback =
        widgetOptions[0]?.value || DEFAULT_WIDGET_OPTIONS[0].value;
      setSelectedWidget(fallback);
    }
  }, [selectedWidget, widgetOptions]);

  // Ref et état pour le calcul dynamique de l'échelle
  const miniatureContainerRef = useRef(null);
  const widgetContentRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(DEFAULT_CONTAINER_WIDTH);
  const [containerHeight, setContainerHeight] = useState(ORIGINAL_HEIGHT);

  // Observer les dimensions du widgetContent dès le mount (avant isVisible)
  useEffect(() => {
    const el = widgetContentRef.current;
    if (!el) return;
    const update = () => {
      const w = el.offsetWidth - 20;
      const h = el.offsetHeight - 20;
      if (w > 0) setContainerWidth(w);
      if (h > 0) setContainerHeight(h);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const renderDashboardContent = () => {
    if (resolvedViewId) {
      const scaleX = containerWidth / ORIGINAL_WIDTH;
      const scaleY = containerHeight / ORIGINAL_HEIGHT;
      const scale = Math.min(scaleX, scaleY);
      const scaledHeight = ORIGINAL_HEIGHT * scale;

      return (
        <div
          ref={miniatureContainerRef}
          className={S.dashboardMiniatureContainer}
          style={{ height: `${scaledHeight}px` }}
        >
          {isVisible && (
            <div
              className={S.dashboardMiniatureInner}
              style={{
                width: `${ORIGINAL_WIDTH}px`,
                height: `${ORIGINAL_HEIGHT}px`,
                transform: `scale(${scale})`,
              }}
            >
              <AffichageVue
                key={`tdb-${resolvedViewId}`}
                view_id={resolvedViewId}
                mapContainerId={`mapTDB-${resolvedViewId}`}
                uniciteVue={`tdb-min-${resolvedViewId}`}
                skipStorePurge
              />
            </div>
          )}
          <div
            className={S.miniatureSkeletonOverlay}
            style={{ opacity: mapReady ? 0 : 1 }}
          />
        </div>
      );
    }

    if (loading) {
      return (
        <div className={S.widgetLoadingState}>
          <div className={S.widgetLoadingSpinner} />
          <span className={S.widgetLoadingText}>
            {checkMot('chargement_en_cours')}
          </span>
        </div>
      );
    }

    return (
      <div className={S.emptyState}>
        <p className={S.emptyText}>{checkMot('aucun_tableau_de_bord')}</p>
      </div>
    );
  };

  const resolveCenter = (vue) => {
    if (!vue) return null;
    if (Array.isArray(vue.center) && vue.center.length === 2) return vue.center;
    if (vue.lat && vue.lng) return [vue.lat, vue.lng];
    if (vue.latitude && vue.longitude) return [vue.latitude, vue.longitude];
    if (vue.center_lat && vue.center_lng)
      return [vue.center_lat, vue.center_lng];
    return null;
  };

  const renderPlanContent = () => {
    // Si on a un view_id, charger AffichageVue avec tous ses objets (même logique que dashboard)
    if (resolvedViewId) {
      const scaleX = containerWidth / ORIGINAL_WIDTH;
      const scaleY = containerHeight / ORIGINAL_HEIGHT;
      const scale = Math.min(scaleX, scaleY);
      const scaledHeight = ORIGINAL_HEIGHT * scale;

      return (
        <div
          className={S.dashboardMiniatureContainer}
          style={{ height: `${scaledHeight}px` }}
        >
          {isVisible && (
            <div
              className={S.dashboardMiniatureInner}
              style={{
                width: `${ORIGINAL_WIDTH}px`,
                height: `${ORIGINAL_HEIGHT}px`,
                transform: `scale(${scale})`,
              }}
            >
              <AffichageVue
                key={`plan-${resolvedViewId}`}
                view_id={resolvedViewId}
                mapContainerId={`mapPlan-${resolvedViewId}`}
                uniciteVue={`tdb-plan-${resolvedViewId}`}
              />
            </div>
          )}
          <div
            className={S.miniatureSkeletonOverlay}
            style={{ opacity: mapReady ? 0 : 1 }}
          />
        </div>
      );
    }

    // État de chargement
    if (loading) {
      return (
        <div className={S.widgetLoadingState}>
          <div className={S.widgetLoadingSpinner} />
          <span className={S.widgetLoadingText}>
            {checkMot('chargement_en_cours')}
          </span>
        </div>
      );
    }

    // Fallback : carte simple sans objets de vue
    const mapCenter = resolveCenter(firstVue) || [48.8566, 2.3522];
    const mapZoom = firstVue?.zoom || 12;

    return (
      <div className={S.widgetContent}>
        {hasVue ? (
          <div className={S.planContainer}>
            <div className={S.planPreview}>
              <MapPreview
                center={mapCenter}
                zoom={mapZoom}
                className={S.leafletContainer}
                interactive={true}
              />
            </div>
          </div>
        ) : (
          <div className={S.emptyState}>
            <div className={S.mapPlaceholder}>
              <MapPreview
                center={[48.8566, 2.3522]}
                zoom={3}
                className={S.leafletContainer}
                interactive={true}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const resolvePlotPayload = (graphe) => {
    if (!graphe) return null;

    // Nova estrutura da API real (viewobj_type: 2)
    if (graphe.data && graphe.data.data && Array.isArray(graphe.data.data)) {
      const plotData = graphe.data.data.map((series, index) => {
        const xData =
          series.x && Array.isArray(series.x)
            ? series.x.map((timestamp) =>
                typeof timestamp === 'number'
                  ? new Date(timestamp * 1000)
                  : timestamp
              )
            : [];

        return {
          x: xData,
          y: series.y || [],
          type: series.type || 'scatter',
          mode: series.mode || 'lines',
          name: series.name || series.prefix || `Série ${index + 1}`,
          line: {
            color: series.color || undefined,
            dash: series.line?.dash || 'solid',
            shape: series.line?.shape || 'linear',
          },
        };
      });

      return {
        data: plotData,
        layout: {
          title: graphe.data.config?.title || '',
          margin: { l: 40, r: 20, t: 30, b: 40 },
          autosize: true,
          showlegend: false,
          paper_bgcolor: 'rgba(0,0,0,0)',
          plot_bgcolor: 'rgba(0,0,0,0)',
          font: { color: '#4c4c4c', size: 12 },
          height: undefined,
          xaxis: {
            type: graphe.data.config?.xaxis?.type || 'date',
            title: graphe.data.config?.xaxis?.title || '',
          },
          yaxis: {
            title: graphe.data.config?.yaxis?.title || '',
          },
        },
      };
    }

    // Estrutura antiga (compatibilidade retroativa)
    if (graphe.data && Array.isArray(graphe.data) && graphe.config) {
      const plotData = graphe.data.map((series, index) => {
        const xData =
          series.x && Array.isArray(series.x)
            ? series.x.map((timestamp) =>
                typeof timestamp === 'number'
                  ? new Date(timestamp * 1000)
                  : timestamp
              )
            : [];

        return {
          x: xData,
          y: series.y || [],
          type: series.type || 'scatter',
          mode: series.mode || 'lines',
          name: series.name || series.prefix || `Série ${index + 1}`,
          line: {
            color: series.color || undefined,
            dash: series.line?.dash || 'solid',
            shape: series.line?.shape || 'linear',
          },
        };
      });

      return {
        data: plotData,
        layout: {
          title: graphe.config?.title || '',
          margin: { l: 40, r: 20, t: 30, b: 40 },
          autosize: true,
          showlegend: false,
          paper_bgcolor: 'rgba(0,0,0,0)',
          plot_bgcolor: 'rgba(0,0,0,0)',
          font: { color: '#4c4c4c', size: 12 },
          height: undefined,
          xaxis: {
            type: graphe.config?.xaxis?.type || 'date',
            title: graphe.config?.xaxis?.title || '',
          },
          yaxis: {
            title: graphe.config?.yaxis?.title || '',
          },
        },
      };
    }

    // Fallback vers le format précédent
    const data = graphe.plotData || graphe.data;
    if (!Array.isArray(data) || data.length === 0) return null;

    return {
      data,
      layout: {
        margin: { l: 40, r: 20, t: 30, b: 40 },
        autosize: true,
        showlegend: false,
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { color: '#4c4c4c', size: 12 },
        height: undefined,
      },
    };
  };

  const renderGrapheContent = () => {
    const plotPayload = resolvePlotPayload(firstGraphe);
    const hasPlotData = Boolean(plotPayload);
    const hasGrapheData = hasGraphes || hasViewData;

    if (!hasGrapheData) {
      return (
        <div className={S.emptyState}>
          <p className={S.emptyText}>{checkMot('aucun_graphe_favori')}</p>
        </div>
      );
    }

    return (
      <div className={S.widgetContent}>
        <div className={S.grapheContainer}>
          <div className={S.grapheTitle}>
            {firstGraphe?.graph_name || 'Graphe'}
          </div>
          <div className={S.graphePreview}>
            {hasPlotData ? (
              <div style={{ width: '100%', height: '400px' }}>
                <BibliothequePlotly
                  data={plotPayload.data}
                  layout={plotPayload.layout}
                  config={{ displayModeBar: false, responsive: true }}
                  dimensionHeight={400}
                  dimensionWidth="100%"
                  minimaliste
                  onClick={() => onGrapheClick && onGrapheClick(firstGraphe)}
                />
              </div>
            ) : (
              <div
                style={{
                  height: 400,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                }}
              >
                Pas de données
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSelectedContent = () => {
    if (selectedWidget === 'plan') {
      return renderPlanContent();
    }
    if (selectedWidget === 'graphe') {
      return renderGrapheContent();
    }
    return renderDashboardContent();
  };

  const widgetSizeClass =
    sizeVariant === 'medium'
      ? S.widgetMedium
      : sizeVariant === 'small'
        ? S.widgetSmall
        : S.widgetLarge;

  const resolvedHeader =
    headerContent ||
    (showHeader ? (
      <WidgetHeaderControls
        titleKey={currentWidget.labelKey}
        selectOptions={widgetOptionsWithDisabled}
        selectedValue={selectedWidget}
        onSelectChange={setSelectedWidget}
        headerAction={
          resolvedViewId ? (
            <button
              className={S.widgetMenuBtn}
              title={checkMot('voir_plus')}
              onClick={ouvrirPopup}
            >
              <FiExternalLink size={18} />
            </button>
          ) : null
        }
      />
    ) : null);

  return (
    <>
      <div
        ref={widgetRef}
        className={[S.widget, widgetSizeClass, S.widgetDashboard].join(' ')}
      >
        {resolvedHeader}
        <div ref={widgetContentRef} className={S.dashboardWidgetContent}>
          {renderSelectedContent()}
        </div>
      </div>
      <Popup
        sansBouton={true}
        eventALaFermeture={fermerPopup}
        showModal={afficherPopup}
      >
        {afficherPopup && (
          <AffichageVue
            view_id={resolvedViewId}
            mapContainerId={`mapPopupTDB-${resolvedViewId}`}
            uniciteVue={`tdb-pop-${resolvedViewId}`}
            skipStorePurge
          />
        )}
      </Popup>
    </>
  );
};

export default WidgetTableauDeBord;
